import { useEffect, useRef, useState } from "react";
import { Search, Droplet } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {getBulkDropdownData, getOtherRefSearchDropdown} from "../../api/keyvalue";
import {
    addFuelRequestData,
    approveFuelRequestByApprover,
    approveFuelRequestByPumper,
    getFuelRequestDataById,
    rejectFuelRequestByApprover,
    rejectFuelRequestByPumper,
    searchFuelRequest,
} from "../../api/FuelRequestApi";
import Grid from "../../components/Grid";
import Button from "../../components/input/Button";
import SideDrawer from "../../components/input/SideDrawer";
import LoadingSpinner from "../../components/LoadingSpinner";
import FuelRequestComponent from "../../components/transaction/FuelRequestComponent";
import {
    DateOnlyCellRenderer,
    ResizableScrollingTextRenderer,
    StatusCellStyle
} from "../../components/renderer/StyleRenderer";
import Tab from "../../components/Tab";
import { fuelRequestValidation } from "../../components/validations/FuelRequestCreation";
import { FORMS, MODULES } from "../../const";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import { errorActions } from "../../store/error-slice";
import {getFormAuthority} from "../../auth/authHelper";
import Dropdown from "../../components/input/Dropdown";
import SearchDropdown from "../../components/input/SearchDropdown";
import {formatFromJsonDate} from "../../dateConfig";

const FORM = FORMS.FUEL_REQUEST.HEADER.form;

const getFuelRequestStage = (data) => {
    if (data.frqRequestStatus === "Rejected") return "view";
    if (data.frqRequestStatus === "Pending") return "approve";
    if (data.frqRequestStatus === "Approved" && data.frqTopUpStatus === "Pending")
        return "pumper";
    return "view";
};

const STAGE_LABEL = {
    approve: "Review",
    pumper: "Process",
    view: "View",
};

const STAGE_FORM = {
    approve: FORMS.FUEL_REQUEST.APPROVE.form,
    pumper: FORMS.FUEL_REQUEST.PUMPER.form,
    view: FORMS.FUEL_REQUEST.DETAILS.form,
};

const FuelRequest = () => {
    const { firstViableTab } = useFirstAndLastAuthorizedTab({
        module: MODULES.Transaction,
        section: FORMS.FUEL_REQUEST,
    });

    const [tab, setTab] = useState(1);
    const [drawerTab, setDrawerTab] = useState(1);
    const dispatch = useDispatch();
    const errorState = useSelector((state) => state.error.fuelRequestErrors);
    const auth = useSelector((state) => state.user.permissions);

    const [formMode, setFormMode] = useState("create");
    const [editFuelRequest, setEditFuelRequest] = useState(null);

    const [searchFetchLoading, setSearchFetchLoading] = useState(false);
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
    const [searchRes, setSearchRes] = useState(false);
    const [activeAccordion1, setActiveAccordion1] = useState(true);
    const [resetVisible, setResetVisible] = useState(false);
    const [searchClicked, setSearchClicked] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);

    const [requestStatus, setRequestStatus] = useState(null);
    const [topUpStatus, setTopUpStatus] = useState(null);
    const [statusOptions, setStatusOptions] = useState([]);

    const fuelRequestRef = useRef();
    const jobReferenceRef = useRef(null);
    const requestedByRef = useRef(null);
    const [transporter, setTransporter] = useState("");
    const vehicleNoRef = useRef(null);
    const [searchFieldReset, setSearchFieldReset] = useState(false);
    const gridRef = useRef(null);

    const user = useSelector((state) => state.user);

    useEffect(() => {
        (async () => {
            setIsSearchLoading(true);
            const { status } = await getBulkDropdownData([{ key: "status" }]);
            setStatusOptions(status);
            setIsSearchLoading(false);
        })();
    }, []);

    const handleDrawerOpen = () => {
        dispatch(errorActions.clearFuelRequestError());
        fuelRequestRef.current?.resetForm();
        setIsLoading(false);
        setIsDrawerOpen(true);
        setFormMode("create");
        setEditFuelRequest(null);
        setDrawerTab(1);
        setIsRejecting(false);
    };

    const handleDrawerClose = () => {
        dispatch(errorActions.clearFuelRequestError());
        fuelRequestRef.current?.resetForm();
        setIsDrawerOpen(false);
        setIsRejecting(false);
    };

    const reset = () => {
        fuelRequestRef.current.resetForm();
        setFormMode("create");
        setEditFuelRequest(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitLoading(true);
        dispatch(errorActions.clearFuelRequestError());

        const data = fuelRequestRef.current.getData();
        const errors = fuelRequestValidation(data, formMode);
        if (Object.keys(errors).length > 0) {
            dispatch(errorActions.validateFuelRequest({ fuelRequestRequest: data, formMode }));
            setIsSubmitLoading(false);
            toast.error("There was an error submitting the fuel request", {
                autoClose: 5000,
                containerId: "Main",
            });
            return;
        }

        if (formMode === "create") {
            await addFuelRequestData({ fuelRequestRequest: data, author: user.username });
            toast.success("Fuel request submitted successfully!", {
                autoClose: 5000,
                containerId: "Main",
            });
        }
        setIsSubmitLoading(false);
        reset();
        handleDrawerClose();

        await handleSearchSubmit({ preventDefault: () => {} });
    };

    const handleApprove = async (action) => {
        if (action === "reject" && !isRejecting) {
            setIsRejecting(true);
            return;
        }

        const data = fuelRequestRef.current.getData();

        const errors = fuelRequestValidation(data, formMode, isRejecting);
        if (Object.keys(errors).length > 0) {
            dispatch(errorActions.validateFuelRequest({ fuelRequestRequest: data, formMode }));
            toast.error(
                action === "reject" ? "Reject remark is mandatory!" : "There was an error submitting the fuel request",
                { autoClose: 5000, containerId: "Main" }
            );
            return;
        }

        setIsSubmitLoading(true);

        try {
            if (formMode === "approve") {
                action === "approve"
                    ? await approveFuelRequestByApprover({
                        frqId: data.frqId,
                        frqApprovedAmount: data.frqApprovedAmount,
                        frqApproverRemarks: data.frqApproverRemarks,
                        author: user.username,
                    })
                    : await rejectFuelRequestByApprover({
                        frqId: data.frqId,
                        frqRejectRemarks: data.frqRejectRemarks,
                        author: user.username,
                    });
            } else if (formMode === "pumper") {
                action === "approve"
                    ? await approveFuelRequestByPumper({
                        frqId: data.frqId,
                        frqTopUpAmount: data.frqTopUpAmount,
                        frqPumperRemarks: data.frqPumperRemarks,
                        author: user.username,
                    })
                    : await rejectFuelRequestByPumper({
                        frqId: data.frqId,
                        frqRejectRemarks: data.frqRejectRemarks,
                        author: user.username,
                    });
            }
            toast.success(`Fuel request ${action === "approve" ? "approved" : "rejected"}!`, {
                autoClose: 5000,
                containerId: "Main",
            });
        } catch (error) {
            console.error("Action failed", error);
            setIsSubmitLoading(false);
            return;
        }

        setIsSubmitLoading(false);
        handleDrawerClose();
        await handleSearchSubmit({ preventDefault: () => {} });
    };

    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        setActiveAccordion1(false);
        setResetVisible(true);
        setSearchClicked(true);
        setSearchSubmitLoading(true);

        const response = await searchFuelRequest({
            frqJobReference: jobReferenceRef.current.value,
            frqRequestedBy: requestedByRef.current.value,
            frqRequestStatus: requestStatus,
            frqTopUpStatus: topUpStatus,
            frqTransporter: transporter,
            frqVehicleNo: vehicleNoRef.current.value,
        });
        setSearchRes(response);
        setSearchSubmitLoading(false);
    };

    const handleSearchReset = (e) => {
        e.preventDefault();
        jobReferenceRef.current.value = "";
        requestedByRef.current.value = "";
        vehicleNoRef.current.value = "";
        setRequestStatus(null);
        setTopUpStatus(null);
        setTransporter("");
        setSearchFieldReset(true);
        setSearchRes(false);
    };

    const openRowInMode = async (rowData) => {
        const stage = getFuelRequestStage(rowData);

        if (stage !== "view") {
            const { authorized } = getFormAuthority(auth[MODULES.Transaction], STAGE_FORM[stage], true);

            if (!authorized) {
                toast.error("You are not authorized to perform this action.", {
                    autoClose: 5000,
                    containerId: "Main",
                });
                return;
            }
        }

        setSearchFetchLoading(true);
        const response = await getFuelRequestDataById(rowData.frqId || rowData.id);
        setEditFuelRequest({
            ...response,
            frqJobReference: response.frqJobReference || rowData.frqJobReference
        });

        setFormMode(stage);
        setIsDrawerOpen(true);
        setSearchFetchLoading(false);
    };

    return (
        <>
            <div
                className="ui attached tabular"
                style={{ display: "flex", justifyContent: "space-around" }}
            >
                <div className="ui tabular menu attached">
                    <div className={`item ${tab === 1 ? "active" : ""}`} onClick={() => setTab(1)}>
            <span>
              <Droplet size={15} style={{ marginRight: "5px" }} />
            </span>
                        Fuel Request
                    </div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }} className="ui tabular menu top attached">
                    <Button
                        template="drawerOpen"
                        onClick={handleDrawerOpen}
                        authenticated={true}
                        create={true}
                        form={FORMS.FUEL_REQUEST.HEADER.form}
                        module={MODULES.Transaction}
                        disabled={firstViableTab === -1}
                        editMode={false}
                    />
                </div>
            </div>

            <div className="ui bottom attached active tab segment" style={{ display: tab === 1 ? "block" : "none" }}>
                <div className="ui styled accordion" style={{ width: "100%" }}>
                    <div
                        className={`title ${activeAccordion1 ? "active" : ""}`}
                        onClick={() => setActiveAccordion1(!activeAccordion1)}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                                <span>Search</span>
                                <Search size={14} style={{ marginLeft: "10px" }} />
                                <i className="dropdown icon"></i>
                            </div>
                            <i
                                className="refresh icon primary button"
                                style={{ display: resetVisible ? "flex" : "none", cursor: "pointer" }}
                                onClick={() => {}}
                            ></i>
                        </div>
                    </div>
                    <div className={`content ${activeAccordion1 ? "active" : ""}`}>
                        {isSearchLoading ? (
                            <LoadingSpinner />
                        ) : (
                            <form className="ui form">
                                <h4 className="ui diving header">Fuel Request Information</h4>
                                <div className="three fields">
                                    <div className="field">
                                        <label>Request Number</label>
                                        <input type="text" placeholder="Request Number" ref={jobReferenceRef} />
                                    </div>
                                    <div className="field">
                                        <Dropdown
                                            label="Request Status"
                                            options={statusOptions}
                                            placeholder="All Status"
                                            value={requestStatus}
                                            onChange={(e, data) => setRequestStatus(data.value)}
                                        />
                                    </div>
                                    <div className="field">
                                        <Dropdown
                                            label="Top Up Status"
                                            options={statusOptions}
                                            placeholder="All Status"
                                            value={topUpStatus}
                                            onChange={(e, data) => setTopUpStatus(data.value)}
                                        />
                                    </div>
                                </div>
                                <div className="three fields">
                                    <div className="field">
                                        <label>Requested By</label>
                                        <input type="text" placeholder="Requested By" ref={requestedByRef} />
                                    </div>
                                    <div className="field">
                                        <SearchDropdown
                                            key={1}
                                            editMode={false}
                                            reset={searchFieldReset}
                                            setReset={setSearchFieldReset}
                                            clearable={true}
                                            onClear={() => setTransporter("")}
                                            onResultSelect={(data) => setTransporter(data)}
                                            api={getOtherRefSearchDropdown}
                                            apiDependency={{
                                                type: "stakeholder_type",
                                                value: "Transporter",
                                            }}
                                            label="Transporter"
                                        />
                                    </div>
                                    <div className="field">
                                        <label>Vehicle No</label>
                                        <input type="text" placeholder="Vehicle No" ref={vehicleNoRef} />
                                    </div>
                                </div>
                                <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                                    <div className="field" style={{ width: "100%" }}>
                                        <Button template="search" onClick={handleSearchSubmit} />
                                        <Button template="searchReset" onClick={handleSearchReset} />
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </div>

                {searchClicked &&
                    (searchSubmitLoading ? (
                        <LoadingSpinner />
                    ) : (
                        <Grid
                            ref={gridRef}
                            heading="Fuel Requests"
                            isLoading={searchFetchLoading}
                            content={searchRes}
                            showDownloadButton={true}
                            downloadParam=""
                            downloadAction="Fuel Request Excel Download"
                            reportName="Fuel-Request-Details.csv"
                            downloadOnlySelected={true}
                            multiRowSelection={true}
                            excelSheetDownload={true}
                            config={[
                                { headerName: "Id", field: "frqId", sort: "desc", hide: true },
                                { headerName: "Request Number",pinned: "left", field: "frqJobReference", checkboxSelection: true, headerCheckboxSelection: true },
                                { headerName: "Requested By", field: "frqRequestedBy" },
                                { headerName: "Requested Amount (L)", field: "frqRequestedAmount" },
                                {
                                    headerName: "Required Date",
                                    field: "frqRequiredDate",
                                    cellRenderer: DateOnlyCellRenderer
                                },
                                { headerName: "Approved Amount (L)", field: "frqApprovedAmount" },
                                { headerName: "Top Up Amount (L)", field: "frqTopUpAmount" },
                                { headerName: "Approved Liters", field: "frqFinalAmount" },
                                { headerName: "Transporter", field: "frqTransporterName", cellRenderer: ResizableScrollingTextRenderer },
                                { headerName: "Vehicle No", field: "frqVehicleNo" },
                                { headerName: "Card No", field: "frqCardNo" },
                                { headerName: "Km", field: "frqTripDistance" },
                                { headerName: "Remark", field: "frqRemark",cellRenderer: ResizableScrollingTextRenderer },
                                { headerName: "Request Status", pinned: "right",field: "frqRequestStatus", cellRenderer: StatusCellStyle },
                                { headerName: "Top Up Status",pinned: "right", field: "frqTopUpStatus", cellRenderer: StatusCellStyle },
                                {
                                    headerName: "Action",
                                    field: "action",
                                    pinned: "right",
                                    cellRenderer: (props) => {
                                        const stage = getFuelRequestStage(props.data);
                                        return (
                                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                                                <Button
                                                    template={stage}
                                                    label={STAGE_LABEL[stage]}
                                                    editMode={true}
                                                    onClick={() => openRowInMode(props.data)}
                                                />
                                            </div>
                                        );
                                    },
                                }
                            ]}
                            onCellDoubleClicked={(props) => openRowInMode(props.data)}
                        />
                    ))}
            </div>

            <SideDrawer
                open={isDrawerOpen}
                handleDrawerClose={handleDrawerClose}
                head={
                    formMode !== "create" ? (
                        <div className="fuel-request-header" style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}>
                            <h3>Request Number: {editFuelRequest?.frqJobReference}</h3>
                        </div>
                    ) : null
                }
                body={
                    <form className="drawer-content" onSubmit={handleSubmit}>
                        <div style={{ width: "60vw", boxShadow: "none" }}>
                            <div className="ui top attached tabular menu">
                                <Tab
                                    Icon={Droplet}
                                    onClick={() => setDrawerTab(1)}
                                    active={drawerTab === 1}
                                    errors={Object.keys(errorState.fuelRequestErrors).length !== 0}
                                    label="Fuel Request"
                                    authenticated={true}
                                    form={FORMS.FUEL_REQUEST.DETAILS.form}
                                    showMandatory={true}
                                    isMandatory={true}
                                />
                            </div>
                            <div
                                className="ui bottom attached active tab segment"
                                style={{ display: drawerTab === 1 ? "block" : "none" }}
                            >
                                <FuelRequestComponent
                                    ref={fuelRequestRef}
                                    isLoading={isLoading}
                                    errors={errorState.fuelRequestErrors}
                                    formMode={formMode}
                                    currentValues={formMode !== "create" ? editFuelRequest : null}
                                    isRejecting={isRejecting}
                                />
                            </div>
                            <div
                                style={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    gap: "8px",
                                }}
                            >
                                {formMode === "create" && (
                                    <>
                                        <Button
                                            label="Reset"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                fuelRequestRef.current?.resetForm();
                                            }}
                                        />
                                        <Button
                                            type="submit"
                                            template="create"
                                            isLoading={isSubmitLoading}
                                            create={true}
                                            form={FORMS.FUEL_REQUEST.HEADER.form}
                                            module={MODULES.Transaction}
                                            section={FORMS.FUEL_REQUEST}
                                            authenticated={true}
                                            editMode={false}
                                        />
                                    </>
                                )}
                                {(formMode === "approve" || formMode === "pumper") && (
                                    <>
                                        {!isRejecting && (
                                            <Button
                                                label="Approve"
                                                template="approve"
                                                isLoading={isSubmitLoading}
                                                editMode={true}
                                                onClick={() => handleApprove("approve")}
                                            />
                                        )}
                                        <Button
                                            template="reject"
                                            label={isRejecting ? "Confirm Reject" : "Reject"}
                                            isLoading={isSubmitLoading}
                                            editMode={true}
                                            onClick={() => handleApprove("reject")}
                                        />
                                        {isRejecting && (
                                            <Button
                                                label="Cancel"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setIsRejecting(false);
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </form>
                }
                style={{ width: "60vw", boxShadow: "none" }}
            />
        </>
    );
};
export default FuelRequest;